import {
    ChangeDetectionStrategy,
    Component,
    computed,
    Directive,
    inject,
    input,
    model,
    output,
    Signal,
} from '@angular/core';
import { LoadingCardComponent } from '@app/v2/components/loading-card/loading-card.component';
import { MdsButtonComponent } from '@medpacesoftwaredevelopment/designsystem';
import { EditedCard } from '@models/edited-card';
import { CurrentlyEditedCardService } from '@services/currently-edited-card/currently-edited-card.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
export type CardMode = 'edit' | 'view';
@Component({
    selector: 'card',
    imports: [MdsButtonComponent, LoadingCardComponent],
    templateUrl: './card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent {
    public readonly currentlyEditedCardService = inject(CurrentlyEditedCardService);
    public readonly snackbarService = inject(SnackbarService);

    public readonly title = input.required<string>();
    public readonly mode = model.required<CardMode>();
    public readonly showEditSaveButton = input<boolean>(true);
    public readonly showCancelButton = input<boolean>(false);
    public readonly showSharedContent = input<boolean>(false);
    public readonly isLoading = input<boolean>(false);
    public readonly showTitleAsteriskWhenEditMode = input(true);
    public readonly changeModeFn = input<
        (mode: CardMode, setModeFn: (mode: CardMode) => void, cancel?: boolean) => void
    >((mode, setModeFn, cancel) => setModeFn(mode));
    public readonly save = output<void>();
    public readonly cancel = output<void>();

    public readonly currentCard: Signal<EditedCard> = computed(() => ({ cardName: this.title(), cardForm: this }));

    protected tryChangeMode(mode: CardMode, cancel: boolean = false) {
        this.changeModeFn()(mode, this.setMode.bind(this), cancel);
    }
    onCancel() {
        this.cancel.emit();
        this.setMode('view');
    }
    private setMode(mode: CardMode) {
        if (this.setupCurrentCard(mode)) {
            this.mode.set(mode);
            if (mode === 'view') this.save.emit();
        }
    }

    setupCurrentCard(mode: CardMode): boolean {
        let result: boolean = true;
        if (mode === 'view') {
            //Current mode 'edit', throw an error if other card is currently edited
            if (!this.currentlyEditedCardService.clearCurrentState(this.currentCard())) {
                throw Error(
                    `Incorrect currently edited card, should be '${this.currentCard().cardName}', got '${
                        this.currentlyEditedCardService.getState().cardName
                    }'`
                );
            }
        } else if (mode === 'edit') {
            //current mode 'view'
            //set this card as current state, if failed show a snackbar message
            if (!this.currentlyEditedCardService.setState(this.currentCard())) {
                result = false;
                this.snackbarService.openInfoSnackbar(
                    `Card '${this.currentCard().cardName}' cannot be edited, currently editing: '${
                        this.currentlyEditedCardService.getState().cardName
                    }'`
                );
            }
        }
        return result;
    }
}

@Directive({
    selector: 'card-edit-mode-content',
    standalone: true,
})
export class CardEditModeContentDirective {}
@Directive({
    selector: 'card-view-mode-content',
    standalone: true,
})
export class CardViewModeContentDirective {}

@Directive({
    selector: 'card-shared-content',
    standalone: true,
})
export class CardSharedContentDirective {}

@Directive({
    selector: 'card-header-additional-content',
    standalone: true,
})
export class CardHeaderAdditionalContentDirective {}
